import { supabase } from './supabase';

/**
 * Notify all followers of a user about an action they performed.
 * This is called after the user creates a post, comment, listing, etc.
 */
export async function notifyFollowers({
  userId,
  username,
  avatarUrl,
  type,
  title,
  message,
  data,
}: {
  userId: string;
  username: string;
  avatarUrl?: string;
  type: string;
  title: string;
  message: string;
  data: Record<string, any>;
}) {
  try {
    // Get all followers of this user
    const { data: followers, error } = await supabase
      .from('follows')
      .select('follower_id')
      .eq('following_id', userId);

    if (error || !followers || followers.length === 0) return;

    // Create notifications for all followers (batch insert)
    const notifications = followers.map(f => ({
      user_id: f.follower_id,
      type,
      title,
      message,
      data: {
        ...data,
        sender_id: userId,
        sender_username: username,
        sender_avatar_url: avatarUrl,
      },
    }));

    // Insert in batches of 50 to avoid hitting limits
    for (let i = 0; i < notifications.length; i += 50) {
      const batch = notifications.slice(i, i + 50);
      await supabase.from('notifications').insert(batch);
    }
  } catch (err) {
    console.error('Error notifying followers:', err);
  }
}

/**
 * Notify all users subscribed to community event notifications.
 */
export async function notifyCommunityEventSubscribers({
  communityId,
  communityName,
  eventTitle,
  eventId,
  creatorId,
  creatorUsername,
  creatorAvatarUrl,
}: {
  communityId: string;
  communityName: string;
  eventTitle: string;
  eventId?: string;
  creatorId: string;
  creatorUsername: string;
  creatorAvatarUrl?: string;
}) {
  try {
    // Get all subscribers for this community (excluding the creator)
    const { data: subscribers, error } = await supabase
      .from('community_event_subscriptions')
      .select('user_id')
      .eq('community_id', communityId)
      .neq('user_id', creatorId);

    if (error || !subscribers || subscribers.length === 0) return;

    const notifications = subscribers.map(s => ({
      user_id: s.user_id,
      type: 'community_event',
      title: `New event in ${communityName}`,
      message: `${creatorUsername} created "${eventTitle}"`,
      data: {
        community_id: communityId,
        community_name: communityName,
        event_id: eventId,
        event_title: eventTitle,
        sender_id: creatorId,
        sender_username: creatorUsername,
        sender_avatar_url: creatorAvatarUrl,
      },
    }));

    for (let i = 0; i < notifications.length; i += 50) {
      const batch = notifications.slice(i, i + 50);
      await supabase.from('notifications').insert(batch);
    }
  } catch (err) {
    console.error('Error notifying community event subscribers:', err);
  }
}
